import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { revealApiKey } from 'services/apiKeyReveal';
import { MdContentCopy, MdCheck, MdOutlineVpnKey } from 'react-icons/md';

interface ApiKeyMeta {
    prefix: string;
    status: 'active' | 'revoked';
    createdAt: string;
    lastUsedAt: string | null;
    revealable?: boolean;
}

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Same create/regenerate/revoke routes as the buyer's ApiKeyPanel (shared ApiKey model, one key
// per account regardless of type — see accountController.ts's requireBuyerOrSeller). Unlike the
// buyer's marketplace key, a seller's key needs no scope: it only ever authenticates POST
// /lead/api/submit for leads belonging to that same seller account, so there's nothing further
// to request or for an admin to approve — no scope section here at all. The same key also
// authenticates the site-generation MCP server (POST /mcp, see seller/mcpDocs). It lives in the
// docs pages of Intégrations (Envoi par API, Site créé avec l'IA); Mon compte only points there.
// `onKeyShown` hands the full key to the host page whenever it's shown (created, regenerated or
// revealed), so the page can insert it in its examples and testers — and '' once it's revoked.
const SellerApiKeyPanel = ({ onKeyShown }: { onKeyShown?: (key: string) => void }) => {
    const [apiKey, setApiKey] = useState<ApiKeyMeta | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [revealedKey, setRevealedKey] = useState<string | null>(null);
    // 'created' = just created/regenerated (copy-it-now warning), 'revealed' = shown on demand.
    const [revealSource, setRevealSource] = useState<'created' | 'revealed'>('created');
    const [copied, setCopied] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'regenerate' | 'revoke' | null>(null);

    const load = () => {
        setLoading(true);
        axios
            .get(`${CONFIG.apiUrl}/account/api-key`, authHeaders())
            .then((res) => setApiKey(res.data?.apiKey ?? null))
            .catch(() => setError('Impossible de charger votre clé API.'))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const handleCopy = () => {
        if (!revealedKey) return;
        navigator.clipboard.writeText(revealedKey).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleCreate = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/account/api-key`, {}, authHeaders());
            setRevealedKey(res.data.apiKey);
            onKeyShown?.(res.data.apiKey);
            setRevealSource('created');
            load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erreur lors de la création de la clé API.');
        } finally {
            setBusy(false);
        }
    };

    const handleRegenerate = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/account/api-key/regenerate`, {}, authHeaders());
            setRevealedKey(res.data.apiKey);
            onKeyShown?.(res.data.apiKey);
            setRevealSource('created');
            load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erreur lors de la régénération de la clé API.');
        } finally {
            setBusy(false);
            setConfirmAction(null);
        }
    };

    const handleReveal = async () => {
        setBusy(true);
        setError('');
        try {
            const key = await revealApiKey();
            setRevealedKey(key);
            onKeyShown?.(key);
            setRevealSource('revealed');
        } catch (e: any) {
            setError(e?.response?.data?.message || "Impossible d'afficher la clé API.");
        } finally {
            setBusy(false);
        }
    };

    const handleRevoke = async () => {
        setBusy(true);
        setError('');
        try {
            await axios.delete(`${CONFIG.apiUrl}/account/api-key`, authHeaders());
            setRevealedKey(null);
            onKeyShown?.('');
            setApiKey(null);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erreur lors de la suppression de la clé API.');
        } finally {
            setBusy(false);
            setConfirmAction(null);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="rounded-xl border border-leadrs-stroke bg-white px-5 py-5">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-purple-soft">
                        <MdOutlineVpnKey className="h-5 w-5 text-leadrs-brand" />
                    </div>
                    <div>
                        <h3 className="font-geist text-base font-semibold text-leadrs-text">Clé API</h3>
                        <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                            Une seule clé par compte : elle sert à l'envoi par API et à vos sites créés avec l'IA.
                            Ne la partagez jamais et ne la publiez pas dans un dépôt de code.
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mt-4 rounded-lg bg-leadrs-red-soft px-4 py-3 font-geist text-sm text-leadrs-red-focus">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="mt-4 py-6 text-center font-geist text-sm text-leadrs-text-soft">Chargement...</div>
                ) : revealedKey ? (
                    <div className="mt-4 rounded-lg border border-leadrs-yellow-focus bg-leadrs-yellow-soft px-4 py-4">
                        <p className="font-geist text-sm font-semibold text-leadrs-text">
                            {revealSource === 'created'
                                ? 'Votre nouvelle clé API — ne la partagez pas et ne la publiez jamais.'
                                : 'Votre clé API — ne la partagez pas et ne la publiez jamais.'}
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                            <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md bg-white px-3 py-2 font-mono text-xs text-leadrs-text">
                                {revealedKey}
                            </code>
                            <button
                                onClick={handleCopy}
                                className="flex items-center gap-1.5 rounded-md bg-leadrs-brand px-3 py-2 font-geist text-xs font-medium text-white hover:opacity-90"
                            >
                                {copied ? <MdCheck className="h-4 w-4" /> : <MdContentCopy className="h-4 w-4" />}
                                {copied ? 'Copié' : 'Copier'}
                            </button>
                        </div>
                        <button
                            onClick={() => setRevealedKey(null)}
                            className="mt-3 font-geist text-xs text-leadrs-text-soft underline hover:text-leadrs-text"
                        >
                            {revealSource === 'created' ? "J'ai copié ma clé" : 'Masquer la clé'}
                        </button>
                    </div>
                ) : !apiKey ? (
                    <div className="mt-4">
                        <button
                            onClick={handleCreate}
                            disabled={busy}
                            className="linear rounded-lg bg-leadrs-brand px-4 py-2 font-geist text-sm font-medium text-white transition duration-200 hover:cursor-pointer disabled:opacity-50"
                        >
                            {busy ? 'Création...' : 'Créer une clé API'}
                        </button>
                    </div>
                ) : (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3">
                        <div>
                            <p className="font-mono text-sm text-leadrs-text">{apiKey.prefix}••••••••••••••••••••</p>
                            <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                                Statut : {apiKey.status === 'active' ? 'Active' : 'Désactivée'} · Dernière utilisation :{' '}
                                {apiKey.lastUsedAt ? new Date(apiKey.lastUsedAt).toLocaleString('fr-FR') : 'jamais'}
                            </p>
                        </div>
                        {confirmAction ? (
                            <div className="flex items-center gap-2">
                                <span className="font-geist text-xs text-leadrs-text-soft">
                                    {confirmAction === 'regenerate'
                                        ? "Régénérer ? L'ancienne clé cessera de fonctionner immédiatement."
                                        : 'Révoquer définitivement cette clé ?'}
                                </span>
                                <button
                                    onClick={confirmAction === 'regenerate' ? handleRegenerate : handleRevoke}
                                    disabled={busy}
                                    className="rounded-md bg-leadrs-red-focus px-3 py-1.5 font-geist text-xs font-medium text-white disabled:opacity-50"
                                >
                                    {busy ? '...' : 'Confirmer'}
                                </button>
                                <button
                                    onClick={() => setConfirmAction(null)}
                                    className="rounded-md bg-leadrs-bg px-3 py-1.5 font-geist text-xs font-medium text-leadrs-text-soft hover:opacity-80"
                                >
                                    Annuler
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleReveal}
                                    disabled={busy}
                                    title={apiKey.revealable === false ? "Clé créée avant l'affichage à la demande : régénérez-la une fois pour pouvoir l'afficher." : undefined}
                                    className="rounded-md border border-leadrs-stroke px-3 py-1.5 font-geist text-xs font-medium text-leadrs-text hover:bg-leadrs-bg disabled:opacity-50"
                                >
                                    {busy ? '...' : 'Afficher'}
                                </button>
                                <button
                                    onClick={() => setConfirmAction('regenerate')}
                                    className="rounded-md border border-leadrs-stroke px-3 py-1.5 font-geist text-xs font-medium text-leadrs-text hover:bg-leadrs-bg"
                                >
                                    Régénérer
                                </button>
                                <button
                                    onClick={() => setConfirmAction('revoke')}
                                    className="rounded-md border border-leadrs-red-focus px-3 py-1.5 font-geist text-xs font-medium text-leadrs-red-focus hover:bg-leadrs-red-soft"
                                >
                                    Révoquer
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SellerApiKeyPanel;
